import { boundedInteger, boundedText, pathSegment, withQuery } from './query';
import type { ActivityFilters } from '@/features/activity/types';
import type { DemosFilters } from '@/features/demos/types';
import type { ProjectsFilters } from '@/features/projects/types';
import type { TasksFilters } from '@/features/tasks/types';

export const projectPaths = {
  list: (filters: ProjectsFilters = {}) => withQuery('/api/projects', { dept: boundedText(filters.dept, 64) }),
  create: '/api/projects',
  update: (id: string) => `/api/projects/${pathSegment(id)}`,
  remove: (id: string) => `/api/projects/${pathSegment(id)}`,
  upload: '/api/projects/upload',
} as const;

export const taskPaths = {
  list: (filters: TasksFilters = {}) => withQuery('/api/tasks', {
    projectId: boundedText(filters.projectId, 128),
    status: boundedText(filters.status, 32),
    dept: boundedText(filters.dept, 64),
    assignedTo: boundedText(filters.assignedTo, 128),
  }),
  mine: '/api/tasks/my',
  detail: (id: string) => `/api/tasks/${pathSegment(id)}`,
  create: '/api/tasks',
  update: (id: string) => `/api/tasks/${pathSegment(id)}`,
  remove: (id: string) => `/api/tasks/${pathSegment(id)}`,
} as const;

export const demoPaths = {
  list: (filters: DemosFilters = {}) => withQuery('/api/demos', {
    status: boundedText(filters.status, 32), priority: boundedText(filters.priority, 32),
    mine: filters.mine ? 1 : undefined,
  }),
  analytics: '/api/demos/analytics', detail: (id: string) => `/api/demos/${pathSegment(id)}`,
  create: '/api/demos', update: (id: string) => `/api/demos/${pathSegment(id)}`,
  assign: (id: string) => `/api/demos/${pathSegment(id)}/assign`,
  comments: (id: string) => `/api/demos/${pathSegment(id)}/comments`,
  feedback: (id: string) => `/api/demos/${pathSegment(id)}/feedback`,
  remove: (id: string) => `/api/demos/${pathSegment(id)}`,
} as const;

export const dcmsPaths = {
  list: '/api/dcms', create: '/api/dcms',
  update: (id: string) => `/api/dcms/${pathSegment(id)}`,
  remove: (id: string) => `/api/dcms/${pathSegment(id)}`,
} as const;

export const timeLogPaths = {
  list: (filters: { limit?: number; userId?: string; from?: string; to?: string } = {}) => withQuery('/api/time-logs', {
    limit: boundedInteger(filters.limit, 30, 1, 90), userId: boundedText(filters.userId, 128),
    from: boundedText(filters.from, 10), to: boundedText(filters.to, 10),
  }),
  today: (userId?: string) => withQuery('/api/time-logs/today', { userId: boundedText(userId, 128) }),
  team: (department?: string) => withQuery('/api/time-logs/team', { dept: boundedText(department, 64) }),
  dashboard: '/api/time-logs/dashboard', checkIn: '/api/time-logs/checkin',
  checkOut: '/api/time-logs/checkout', startBreak: '/api/time-logs/breaks/start',
  endBreak: '/api/time-logs/breaks/end',
} as const;

export const activityPaths = {
  list: (filters: ActivityFilters) => withQuery('/api/activity', {
    action: boundedText(filters.action, 64), from: boundedText(filters.from, 30),
    to: boundedText(filters.to, 30), userId: boundedText(filters.userId, 128),
    limit: boundedInteger(filters.limit, 100, 1, 200),
    offset: boundedInteger(filters.offset, 0, 0, 1_000_000),
  }),
  mine: '/api/activity/mine',
  entity: (type: string, id: string) => `/api/activity/entity/${pathSegment(type)}/${pathSegment(id)}`,
  comment: '/api/activity/comment',
} as const;

export const notificationPaths = {
  list: (limit = 20) => withQuery('/api/notifications', { limit: boundedInteger(limit, 20, 1, 100) }),
  markRead: (id: string) => `/api/notifications/${pathSegment(id)}/read`,
  markAllRead: '/api/notifications/read-all',
} as const;

export const aiPaths = {
  briefing: '/api/ai/briefing', leadScores: '/api/ai/lead-scores',
  callOptimizer: '/api/ai/call-optimizer', nicheIntelligence: '/api/ai/niche-intelligence',
  anomalies: '/api/ai/anomalies', scrapeAdvisor: '/api/ai/scrape-advisor',
  chat: '/api/ai/chat', followUpDraft: '/api/ai/follow-up-draft',
  meetingAgenda: '/api/ai/meeting-agenda', leadInsights: '/api/ai/lead-insights',
  callCoach: '/api/ai/call-coach', parseIntent: '/api/ai/parse-intent',
} as const;

export const uploadPaths = { file: '/api/upload', project: '/api/projects/upload' } as const;
